import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import { useQuery } from "@tanstack/react-query";
import apiCall from "../../../../utils/apiCall";
import ResetStudentForm from "./ResetStudentForm";
// import { AiOutlineSearch as SearchIcon } from "react-icons/ai";
import { IoReload as ReloadIcon } from "react-icons/io5";
import { format } from "date-fns";
import InputAdornment from "@mui/material/InputAdornment";
import { green } from "@mui/material/colors";


const ROWSTYLE = {
  "&:hover td": {
    backgroundColor: "var(--table-highlight)",
  },
};

const HeadStyle = {
  fontWeight: "bold",
  borderBottom: "2px solid var(--dark-green-color)",
  padding: "15px 5px",
};
interface ResetStudentFormProps {
  open: boolean;
  onClose: () => void;
  applicantId?: number | null; // <-- add this line
}


const ResetApplicantPassword = () => {
  const [search, setSearch] = useState("");
  const [routeId, setRouteId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedApplicantId, setSelectedApplicantId] = useState<number | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuApplicantId, setMenuApplicantId] = useState<number | null>(null);

  const itemsPerPage = 50;

  /** ROUTES */
  const { data: routes = [], isLoading: routesLoading } = useQuery({
    queryKey: ["routes"],
    queryFn: async () => {
      const res = await apiCall().get("/Routes/GetAllRoute");
      return res.data;
    },
    select: (data) => data.Data,
  });

  /** APPLICANTS */
  const { data, isLoading } = useQuery({
    queryKey: ["student-reset", currentPage, itemsPerPage, search, routeId],
    queryFn: async () => {
      const res = await apiCall().get("/Applicant/GetAllApplicant", {
        params: {
          PageNumber: currentPage,
          PageSize: itemsPerPage,
          searchTerm: search || undefined,
          RouteId: routeId || undefined,
        },
      });
      return res.data;
    },
    keepPreviousData: true,
  });

  const applicants = data?.Data ?? [];
  const totalSize = data?.TotalSize ?? 0;
  const totalPages = Math.ceil(totalSize / itemsPerPage);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (_: any, value: number) => {
    setCurrentPage(value);
  };

  /** Menu Handlers */
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, applicantId: number) => {
    setAnchorEl(event.currentTarget);
    setMenuApplicantId(applicantId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuApplicantId(null);
  };

  const handleResetPassword = () => {
    setSelectedApplicantId(menuApplicantId);
    setModalOpen(true);
    handleMenuClose();
  };

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Reset Applicant Password in Menu Option</Typography>
      </Box>

      {/* Filters */}
      <Box display="flex" gap={2} mb={2}>
        <TextField
          variant="outlined"
          size="small"
          // sx={{ border: "1px solid green", fontWeight: "bold" }}

          placeholder="Search with Email, Name"
          value={search}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Route Filter */}
        <TextField
          select
          size="small"
          sx={{ border: "2px solid green", fontWeight: "bold" }}
          value={routeId ?? ""}
          onChange={(e) => {
            setRouteId(e.target.value ? Number(e.target.value) : null);
            setCurrentPage(1);
          }}
          SelectProps={{ native: true }}
          disabled={routesLoading}
        >
          <option value="">All Routes</option>
          {routes.map((route: any) => (
            <option key={route.RouteId} value={route.RouteId}>
              {route.RouteName}
            </option>
          ))}
        </TextField>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={HeadStyle}>Applicant ID</TableCell>
                  <TableCell sx={HeadStyle}>First Name</TableCell>
                  <TableCell sx={HeadStyle}>Last Name</TableCell>
                  <TableCell sx={HeadStyle}>Email</TableCell>
                  <TableCell sx={HeadStyle}>Phone Number</TableCell>
                  <TableCell sx={HeadStyle}>Route Name</TableCell>
                  <TableCell sx={{ ...HeadStyle, color: green[500] }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {applicants.map((student: any) => (
                  <TableRow key={student.ApplicantId} sx={ROWSTYLE}>
                    <TableCell>{student.ApplicantId}</TableCell>
                    <TableCell>{student.FirstName}</TableCell>
                    <TableCell>{student.LastName}</TableCell>
                    <TableCell>{student.Email}</TableCell>
                    <TableCell>{student.PhoneNumber}</TableCell>
                    <TableCell>{student.RouteName}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => handleMenuClick(e, student.ApplicantId)}
                      >
                        <MoreVertIcon sx={{ color: green[800] }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {applicants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No applicants found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} />
          </Box>

          {/* Menu */}
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem sx={{ color: green[500] }} onClick={handleResetPassword}>Reset Password</MenuItem>
          </Menu>

          {/* Reset Modal */}
          <ResetStudentForm
            open={modalOpen}
            applicantId={selectedApplicantId}
            onClose={() => {
              setModalOpen(false);
              setSelectedApplicantId(null);
            }}
          />
        </>
      )}
    </Container>
  );
};



export default ResetApplicantPassword;
